/**
 * Lab-admin capability keys — the EXACT server capability strings the cockpit gates on. Verified
 * against server/src/modules/lab-admin/{routes,lab-admin.module}.js. `useCan` / nav gating / the
 * per-screen ForbiddenState all key off these. The server re-checks every request; these only
 * hide/disable UI (never the trust boundary). Deny-wins live posture (effective = preset ∪ grants − denies).
 *
 * Two families:
 *   NET-NEW (declared in the lab-admin manifest):  lab_admin.profil.read|manage,
 *                                                  lab_admin.abonnement.read|request
 *   REUSED owner-scoped trio (owned by iam / catalogue / audit manifests):
 *                                                  iam.utilisateur.manage      → staff & permissions
 *                                                  audit.journal.read_all      → journal d'audit
 *                                                  catalogue.parametre.price   → analysis-type pricing
 */
export const ADMIN_CAPS = {
  /** GET the caller-lab's own Laboratoire profile. */
  profilRead: "lab_admin.profil.read",
  /** PATCH the editable profile fields (never id/reference/deploy_class). */
  profilManage: "lab_admin.profil.manage",

  /** Staff roster + membership/grant management (REUSED owner-scoped trio). */
  staffManage: "iam.utilisateur.manage",

  /** The lab-scoped audit journal read (REUSED owner-scoped trio). */
  journalReadAll: "audit.journal.read_all",

  /** Analysis-type price assignment via the M7 ledger (REUSED owner-scoped trio). */
  pricing: "catalogue.parametre.price",

  /** GET the entitlement projection + derived plan/statut/health. */
  abonnementRead: "lab_admin.abonnement.read",
  /** POST an outbound subscription change-request (upgrade/downgrade/cancel). */
  abonnementRequest: "lab_admin.abonnement.request",
} as const;

export type AdminCapability = (typeof ADMIN_CAPS)[keyof typeof ADMIN_CAPS];

/**
 * The known preset ids (mirror of server/src/config/rbac.config.js `presets`). The membership editor
 * offers these as the assignable preset bundles. The OWNER is the `est_proprietaire` flag, NOT a
 * preset — the owner-scoped trio is owner-only and lives in no preset (the server enforces this).
 * The server validates presets against the live config on write; this list only drives the picker UI.
 */
export const STAFF_PRESETS = [
  "assistant",
  "biologiste",
  "automate_agent",
  "manager",
  "comptable",
  "magasinier",
  "preleveur",
  "caissier",
] as const;

export type StaffPreset = (typeof STAFF_PRESETS)[number];
